import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ImageSeparator from '../components/ImageSeparator';
import {
  TrendingUp,
  Target,
  BarChart3,
  Shield,
  CheckCircle,
  Download,
  Calendar,
  Globe,
  Building2,
  Stethoscope,
  Users,
  Activity
} from 'lucide-react';

const Investors = () => {
  const marketData = [
    {
      metric: "Target Market",
      value: "1M+",
      description: "Children in Cameroon & The Gambia aged 2-18"
    },
    {
      metric: "Expansion Potential",
      value: "50M+",
      description: "Children across West Africa"
    },
    {
      metric: "Current Enrollment",
      value: "10,000+",
      description: "Children already enrolled"
    },
    {
      metric: "Growth Rate",
      value: "200%",
      description: "Year-over-year enrollment growth"
    }
  ];

  const unitEconomics = [
    {
      item: "Premium per child",
      amount: "$28 USD (15,820 CFA / 2,030 GMD)",
      description: "Annual premium (starting rate)"
    },
    {
      item: "Expected claims",
      amount: "5,000-6,000 CFA",
      description: "Average annual claims per child"
    },
    {
      item: "Administrative costs",
      amount: "1,000-1,500 CFA",
      description: "Operations, technology, and management"
    },
    {
      item: "Reserves & risk",
      amount: "500-1,000 CFA",
      description: "Risk management and reserves"
    },
    {
      item: "Community revenue (per facility)",
      amount: "Variable",
      description: "Low-cost premium services to surrounding community"
    },
    {
      item: "Net surplus (student only)",
      amount: "500-1,000 CFA",
      description: "Per child annual surplus before community revenue"
    }
  ];

  const roadmap = [
    {
      phase: "Phase 1",
      timeline: "Year 1-2",
      target: "100,000 children",
      description: "Establish operations in 3 major cities, build urgent care facilities, prove unit economics"
    },
    {
      phase: "Phase 2",
      timeline: "Year 3-4",
      target: "500,000 children",
      description: "Expand to 10 cities, launch rural programs, introduce additional services"
    },
    {
      phase: "Phase 3",
      timeline: "Year 5+",
      target: "1M+ children",
      description: "National coverage, regional expansion, technology platform scaling"
    }
  ];

  const riskFactors = [
    {
      category: "Adverse Selection",
      description: "Risk of enrolling children with pre-existing conditions",
      mitigation: "Comprehensive health screening and risk assessment protocols"
    },
    {
      category: "Economic Shocks",
      description: "Economic downturns affecting family ability to pay premiums",
      mitigation: "Sliding scale pricing, donor subsidies, and flexible payment options"
    },
    {
      category: "Counter-Fraud",
      description: "Potential for fraudulent claims or enrollment",
      mitigation: "Biometric ID verification, digital verification systems, and regular audits at our vertically integrated facilities"
    },
    {
      category: "Regulatory Changes",
      description: "Changes in healthcare regulations or insurance requirements",
      mitigation: "Active engagement with regulators, compliance monitoring, and legal expertise"
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative section-padding bg-gradient-to-br from-primary-600 to-accent-600 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="/urgentcare_full_site_plan.png" 
            alt="AMHAS Urgent Care" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Investors</h1>
            <p className="text-xl md:text-2xl text-primary-100">
              World-class urgent care centers + AI telehealth delivering sustainable healthcare
            </p>
          </motion.div>
        </div>
      </section>

      {/* Investment Thesis */}
      <section className="relative section-padding bg-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="/urgentcare_full_site_plan.png" 
            alt="AMHAS Healthcare" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Investment Thesis</h2>
            <p className="section-subtitle">
              AMHAS represents a compelling opportunity to create both social impact and financial returns
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Vertical Integration: Urgent Care + AI Telehealth</h3>
              <p className="text-lg text-gray-600 mb-6">
                AMHAS operates world-class urgent care centers that provide 100% coverage for enrolled students, while also serving the surrounding community at low-cost premium rates. Students can choose between receiving care at our urgent care facilities or via AI-powered telehealth directly in the classroom. This dual-pathway model reduces costs, improves outcomes, and creates multiple revenue streams through vertical integration.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Vertical integration eliminates provider network costs</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Community revenue strengthens long-term sustainability</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Dual care pathways optimize cost and access</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Scalable operations through standardized facility deployment</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Market Opportunity</h3>
              <p className="text-lg text-gray-600 mb-6">
                Cameroon & The Gambia's healthcare markets present significant opportunities for innovative
                solutions that address accessibility and affordability challenges.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Large underserved market with growing demand</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Government support for school-based health programs</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Expansion potential across West Africa</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Separator Banner */}
      <ImageSeparator 
        image="/urgentcare_back_building.png"
        title="Market Overview"
        subtitle="Key metrics and growth indicators for the healthcare market"
      />

      {/* Market Data */}
      <section className="relative section-padding bg-gray-50 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="/urgentcare_side_building.png" 
            alt="AMHAS Healthcare" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Market Overview</h2>
            <p className="section-subtitle">
              Key metrics and growth indicators
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {marketData.map((data, index) => (
              <motion.div
                key={data.metric}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card p-6 text-center"
              >
                <div className="text-3xl font-bold text-primary-600 mb-2">{data.value}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{data.metric}</h3>
                <p className="text-gray-600 text-sm">{data.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Separator Banner */}
      <ImageSeparator 
        image="/urgentcare_lab_work_1.png"
        title="Unit Economics"
        subtitle="Detailed breakdown of costs and revenues per child"
      />

      {/* Unit Economics */}
      <section className="relative section-padding bg-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="/urgentcare_lab_2.png" 
            alt="AMHAS Healthcare" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Unit Economics</h2>
            <p className="section-subtitle">
              Detailed breakdown of costs and revenues per child
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-4">
              {unitEconomics.map((item, index) => (
                <motion.div
                  key={item.item}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                >
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{item.item}</h3>
                    <p className="text-gray-600 text-sm">{item.description}</p>
                  </div>
                  <div className="text-2xl font-bold text-primary-600">{item.amount}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Urgent Care Centers Section */}
      <section className="section-padding bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Urgent Care Centers: Core Infrastructure</h2>
            <p className="section-subtitle">
              World-class facilities engineered for scalability and sustainability
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center mr-4">
                  <Building2 size={32} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Standardized Deployment</h3>
              </div>
              <p className="text-lg text-gray-700 mb-4">
                Each AMHAS Urgent Care Center is engineered to world-class specifications with standardized site plans, 
                allowing rapid deployment while maintaining exceptional quality and low operating costs.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-primary-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">24/7 operation, 365 days a year</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-primary-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Full vertical integration: pharmacy, lab, radiology, observation beds</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-primary-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">U.S.-licensed contractor review for quality assurance</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-primary-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Biometric ID verification for instant coverage validation</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-accent-600 text-white rounded-full flex items-center justify-center mr-4">
                  <Users size={32} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Dual Revenue Model</h3>
              </div>
              <p className="text-lg text-gray-700 mb-4">
                Urgent care centers serve both enrolled students (100% coverage) and the surrounding community 
                (low-cost premium services), creating additional revenue streams that strengthen long-term sustainability.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Students: 100% coverage at no additional cost</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Community: Low-cost premium services generate revenue</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Same world-class facilities and services for all</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Community revenue supports facility sustainability</span>
                </li>
              </ul>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="bg-white rounded-lg p-8 max-w-4xl mx-auto"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Rollout Timeline</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-primary-600 mb-2">+3 Months</div>
                <p className="text-gray-700">Urgent Care Facilities Construction Starts</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-primary-600 mb-2">+6 Months</div>
                <p className="text-gray-700">ID Cards Issued, Facilities Open, Care Begins</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-primary-600 mb-2">+12 Months</div>
                <p className="text-gray-700">All Facilities Open, AI & Telehealth Fully Rolled Out</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Roadmap */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Growth Roadmap</h2>
            <p className="section-subtitle">
              Strategic plan for scaling from 100k to 1M+ children
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {roadmap.map((phase, index) => (
              <motion.div
                key={phase.phase}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card p-8 text-center"
              >
                <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  {index + 1}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{phase.phase}</h3>
                <div className="text-primary-600 font-semibold mb-2">{phase.timeline}</div>
                <div className="text-2xl font-bold text-gray-900 mb-4">{phase.target}</div>
                <p className="text-gray-600 text-sm">{phase.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use of Funds */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Use of Funds</h2>
            <p className="section-subtitle">
              Strategic allocation of investment capital
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                category: "Licensing & Compliance",
                percentage: "15%",
                description: "Regulatory approvals and compliance infrastructure"
              },
              {
                category: "Technology Platform",
                percentage: "20%",
                description: "Digital infrastructure, AI systems, and claims processing"
              },
              {
                category: "Urgent Care Facilities",
                percentage: "30%",
                description: "Construction, equipment, and deployment of world-class urgent care centers"
              },
              {
                category: "School Enrollment & AI/Telehealth",
                percentage: "15%",
                description: "School enrollment programs and AI/telehealth technology deployment"
              },
              {
                category: "Marketing & Outreach",
                percentage: "15%",
                description: "Community engagement and enrollment campaigns"
              },
              {
                category: "Prevention Programs",
                percentage: "15%",
                description: "Health education and preventive care initiatives"
              },
              {
                category: "Working Capital",
                percentage: "10%",
                description: "Operational expenses and reserves"
              }
            ].map((item, index) => (
              <motion.div
                key={item.category}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card p-6 text-center"
              >
                <div className="text-3xl font-bold text-primary-600 mb-2">{item.percentage}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.category}</h3>
                <p className="text-gray-600 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Risk Management */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Governance & Risk Management</h2>
            <p className="section-subtitle">
              Comprehensive risk assessment and mitigation strategies
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {riskFactors.map((risk, index) => (
              <motion.div
                key={risk.category}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card p-8"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-3">{risk.category}</h3>
                <p className="text-gray-600 mb-4">{risk.description}</p>
                <div className="bg-accent-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-accent-800 mb-2">Mitigation Strategy:</h4>
                  <p className="text-accent-700 text-sm">{risk.mitigation}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary-600 text-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-4">Ready to Invest?</h2>
            <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
              Join us in creating a sustainable healthcare model that delivers both social impact and financial returns.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <a href="/schedule-call" className="btn-secondary">
                <Calendar size={20} className="mr-2" />
                Book Investor Call
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Investors;
